import { httpDelete, httpGet, httpPatch, httpPost, httpPut } from "@/src/api/http";
import type { DeleteRes, GetRes, PatchRes, PostRes, Req, Schemas } from "@/src/api/types";

export function getGraph(graphId: number) {
    return httpGet<GetRes<"graphs/{graph_id}/">>(`graphs/${graphId}/`);
}

export function createGraph(workspaceId: number, body: Req<"workspaces/{workspace_id}/graphs/", "post">) {
    return httpPost<PostRes<"workspaces/{workspace_id}/graphs/">>(`workspaces/${workspaceId}/graphs/`, body);
}

export function listGraphs(workspaceId: number) {
    return httpGet<GetRes<"workspaces/{workspace_id}/graphs/">>(`workspaces/${workspaceId}/graphs/`);
}

export function updateGraph(graphId: number, body: Req<"graphs/{graph_id}/", "patch">) {
    return httpPatch<PatchRes<"graphs/{graph_id}/">>(`graphs/${graphId}/`, body);
}

export function deleteGraph(graphId: number) {
    return httpDelete<DeleteRes<"graphs/{graph_id}/">>(`graphs/${graphId}/`);
}

export function deleteGraphs(graphIds: number[]) {
    return httpPost<PostRes<"graphs/delete/">>(`graphs/delete/`, { graph_ids: graphIds });
}

export function moveGraphs(body: Req<"graphs/move/", "post">) {
    return httpPost<PostRes<"graphs/move/">>(`graphs/move/`, body);
}

export function validateGraphAttributes(body: Req<"graphs/validate/", "post">) {
    return httpPost<PostRes<"graphs/validate/">>(`graphs/validate/`, body);
}

export function publishGraph(graphId: number, description = "") {
    return httpPut<void>(`graphs/${graphId}/publish/`, { description });
}

export function cleanUpResults(graphId: number, body: Req<"graphs/{graph_id}/cleanup/", "post">) {
    return httpPost<PostRes<"graphs/{graph_id}/cleanup/">>(`graphs/${graphId}/cleanup/`, body);
}

export function shareGraph(graphId: number, body: Req<"graphs/{graph_id}/share/", "post">) {
    return httpPost<PostRes<"graphs/{graph_id}/share/">>(`graphs/${graphId}/share/`, body);
}

// --- locking ---------------------------------------------------------------------------------

export function getGraphLock(graphId: number) {
    return httpGet<GetRes<"graphs/{graph_id}/lock/">>(`graphs/${graphId}/lock/`);
}

export function lockGraph(graphId: number) {
    return httpPost<PostRes<"graphs/{graph_id}/lock/">>(`graphs/${graphId}/lock/`, {});
}

export function unlockGraph(graphId: number) {
    return httpPost<PostRes<"graphs/{graph_id}/lock/">>(`graphs/${graphId}/lock/`, { unlock: true });
}

// --- permissions -----------------------------------------------------------------------------

export function getGraphPermissions(graphId: number) {
    return httpGet<GetRes<"graphs/{graph_id}/permissions/">>(`graphs/${graphId}/permissions/`);
}

export function getGraphUserPermissions(graphId: number) {
    return httpGet<GetRes<"graphs/{graph_id}/permissions/user/">>(`graphs/${graphId}/permissions/user/`);
}

export function updateGraphPermissions(
    graphId: number,
    mutations: Req<"graphs/{graph_id}/permissions/", "post">,
) {
    return httpPost<void>(`graphs/${graphId}/permissions/`, mutations);
}

// --- versions --------------------------------------------------------------------------------

export function listGraphVersions(graphId: number) {
    return httpGet<GetRes<"graphs/{graph_id}/versions/">>(`graphs/${graphId}/versions/`);
}

export function restoreGraphVersion(graphId: number, versionId: number) {
    return httpPut<Schemas["Graph"]>(`graphs/${graphId}/versions/${versionId}/restore/`, {});
}

// --- ordering --------------------------------------------------------------------------------

export function getGraphOrder(workspaceId: number) {
    return httpGet<number[]>(`workspaces/${workspaceId}/graphs/order/`);
}

export function updateGraphOrder(workspaceId: number, order: number[]) {
    return httpPost<PostRes<"workspaces/{workspace_id}/graphs/order/">>(
        `workspaces/${workspaceId}/graphs/order/`,
        { graph_order: order },
    );
}

// --- results ---------------------------------------------------------------------------------

export function getGraphResults(workspaceId: number) {
    return httpGet<GetRes<"workspaces/{workspace_id}/results/">>(`workspaces/${workspaceId}/results/`);
}

// --- jobs and executions ---------------------------------------------------------------------

export function runJob(graphId: number) {
    return httpGet<GetRes<"graphs/{graph_id}/job/run/">>(`graphs/${graphId}/job/run/`);
}

export function runJobs(graphIds: number[]) {
    return httpPost<PostRes<"graphs/job/run/">>(`graphs/job/run/`, { graph_ids: graphIds });
}

export function cancelJob(graphId: number) {
    return httpPost<PostRes<"graphs/{graph_id}/job_executions/cancel/">>(
        `graphs/${graphId}/job_executions/cancel/`,
        {},
    );
}

/**
 * The `logs` query parameter is presence-only: supplying it switches the response to
 * `JobExecutionWithLogs`, and its value is never read by the backend.
 */
export function listJobExecutions(graphId: number, { count, from = 0 }: { count: number; from?: number }) {
    return httpGet<GetRes<"graphs/{graph_id}/job/job_executions/">>(
        `graphs/${graphId}/job/job_executions/?count=${count}&from=${from}&logs`,
    );
}

export function getLastJobExecution(graphId: number) {
    return listJobExecutions(graphId, { count: 1 });
}

export function countJobExecutions(graphId: number) {
    return httpGet<GetRes<"graphs/{graph_id}/job/job_executions/count/">>(
        `graphs/${graphId}/job/job_executions/count/`,
    );
}

export function getJobExecutionLogs(graphId: number, jobExecutionId: number) {
    return httpGet<GetRes<"graphs/{graph_id}/job/job_executions/{job_execution_id}/logs/">>(
        `graphs/${graphId}/job/job_executions/${jobExecutionId}/logs/`,
    );
}

// --- adherence preview -----------------------------------------------------------------------

export function startAdherencePreview(graphId: number, body: Req<"graphs/{graph_id}/preview-adherence/", "post">) {
    return httpPost<PostRes<"graphs/{graph_id}/preview-adherence/">>(
        `graphs/${graphId}/preview-adherence/`,
        body,
    );
}

export function getAdherencePreviewState(graphId: number) {
    return httpGet<GetRes<"graphs/{graph_id}/preview-adherence/">>(`graphs/${graphId}/preview-adherence/`);
}

// --- discovery -------------------------------------------------------------------------------

export function startDiscovery(body: Req<"graphs/discovery/", "post">) {
    return httpPost<PostRes<"graphs/discovery/">>(`graphs/discovery/`, body);
}

export function getTraces(datamodelConfig: unknown) {
    return httpPost<PostRes<"graphs/traces/">>(`graphs/traces/`, { datamodel_config: datamodelConfig });
}

export function startItdmDiscovery(body: Req<"graphs/itdm_discovery/", "post">) {
    return httpPost<PostRes<"graphs/itdm_discovery/">>(`graphs/itdm_discovery/`, body);
}

export function startTestDrivenDiscovery(body: Req<"graphs/itdm_test_driven/", "post">) {
    return httpPost<PostRes<"graphs/itdm_test_driven/">>(`graphs/itdm_test_driven/`, body);
}

// --- bpmn and training -----------------------------------------------------------------------

export function importBpmn(workspaceId: number, formData: FormData) {
    return httpPost<PostRes<"workspaces/{workspace_id}/bpmn/">>(`workspaces/${workspaceId}/bpmn/`, formData);
}

export function getExerciseGraph(graphId: number) {
    return httpGet<GetRes<"graphs/{graph_id}/exercise/">>(`graphs/${graphId}/exercise/`);
}

export function updateExerciseResult(graphId: number, body: Req<"graphs/{graph_id}/exercise/", "post">) {
    return httpPost<PostRes<"graphs/{graph_id}/exercise/">>(`graphs/${graphId}/exercise/`, body);
}
